/**
 * Exclusive accordion: one collection row expanded at a time.
 * Optional ScrollTrigger maps scroll progress to the active row (theme setting).
 * GSAP loads from theme assets (same-origin) so storefront CSP does not block it.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} [rows]
 *
 * @extends {import('@theme/component').Component<Refs>}
 */
import { Component } from '@theme/component';

/** @type {Promise<void> | undefined} */
let carGsapLoadPromise;

/**
 * @param {string} src
 * @returns {Promise<void>}
 */
function carInjectScript(src) {
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('No document'));
      return;
    }
    const scripts = [...document.getElementsByTagName('script')];
    if (scripts.some((s) => s.src === src)) {
      resolve();
      return;
    }
    const el = document.createElement('script');
    el.src = src;
    el.async = false;
    el.onload = () => resolve();
    el.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(el);
  });
}

/**
 * @param {string} gsapSrc
 * @param {string} stSrc
 */
function carEnsureGsap(gsapSrc, stSrc) {
  if (typeof window === 'undefined') return Promise.reject(new Error('No window'));

  if (window.gsap && window.ScrollTrigger) {
    window.gsap.registerPlugin(window.ScrollTrigger);
    return Promise.resolve();
  }

  if (!carGsapLoadPromise) {
    carGsapLoadPromise = carInjectScript(gsapSrc)
      .then(() => carInjectScript(stSrc))
      .then(() => {
        if (!window.gsap || !window.ScrollTrigger) {
          throw new Error('[collection-accordion-rows] GSAP globals missing after script load');
        }
        window.gsap.registerPlugin(window.ScrollTrigger);
      });
  }

  return carGsapLoadPromise;
}

class CollectionAccordionRows extends Component {
  /** @type {AbortController | undefined} */
  #abort;

  /** @type {import('gsap/ScrollTrigger').ScrollTrigger | undefined} */
  #scrollTrigger;

  /** @type {number} */
  #lastSyncedScrollIndex = -1;

  /** @type {number} */
  #lastScrollProgress = -1;

  /**
   * Bumped in #destroyScrollDriven so async init + rAF do not create triggers after removal
   * (theme editor / morph / hot reload disconnects the host often).
   */
  #scrollLifecycle = 0;

  /** @type {number | undefined} */
  #scrollRafId;

  connectedCallback() {
    super.connectedCallback();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('click', this.#onClick, { signal });

    if (this.dataset.scrollDriven === 'true') {
      void this.#initScrollDriven();
    }
  }

  disconnectedCallback() {
    this.#destroyScrollDriven();
    this.#abort?.abort();
    super.disconnectedCallback();
  }

  /** @returns {HTMLElement[]} */
  #getRows() {
    return Array.from(this.querySelectorAll('[data-accordion-row]')).filter(
      /** @returns {el is HTMLElement} */ (el) => el instanceof HTMLElement
    );
  }

  /**
   * @param {HTMLElement} row
   * @param {boolean} expanded
   */
  #setRowExpanded(row, expanded) {
    const panel = row.querySelector('[data-accordion-panel]');
    const trigger = row.querySelector('[data-accordion-trigger]');
    if (!(panel instanceof HTMLElement) || !(trigger instanceof HTMLButtonElement)) return;

    row.setAttribute('data-expanded', expanded ? 'true' : 'false');
    trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    panel.setAttribute('aria-hidden', expanded ? 'false' : 'true');
    if (expanded) {
      panel.removeAttribute('inert');
    } else {
      panel.setAttribute('inert', '');
    }

    const subtitle = row.querySelector('.collection-accordion-row__trigger-subtitle');
    if (subtitle instanceof HTMLElement) {
      if (expanded) {
        subtitle.removeAttribute('aria-hidden');
      } else {
        subtitle.setAttribute('aria-hidden', 'true');
      }
    }
  }

  /**
   * @param {HTMLElement} row
   */
  #expandExclusive(row) {
    for (const r of this.#getRows()) {
      this.#setRowExpanded(r, r === row);
    }
  }

  /**
   * @param {number} progress 0–1
   */
  #syncRowFromScrollProgress(progress) {
    const rows = this.#getRows();
    const n = rows.length;
    if (n === 0) return;

    const clamped = Math.min(1, Math.max(0, progress));
    const nextProgress = clamped * (n - 1);
    const direction = nextProgress - this.#lastScrollProgress;
    const hysteresis = 0.12;

    let idx = this.#lastSyncedScrollIndex;

    if (idx < 0 || idx > n - 1) {
      idx = Math.round(nextProgress);
    } else {
      const thresholdUp = idx + (0.5 + hysteresis);
      const thresholdDown = idx - (0.5 + hysteresis);

      if (direction >= 0 && nextProgress >= thresholdUp) {
        idx = Math.min(n - 1, idx + 1);
      } else if (direction <= 0 && nextProgress <= thresholdDown) {
        idx = Math.max(0, idx - 1);
      }
    }

    this.#lastScrollProgress = nextProgress;

    if (idx === this.#lastSyncedScrollIndex) return;
    this.#lastSyncedScrollIndex = idx;

    for (let i = 0; i < n; i++) {
      this.#setRowExpanded(rows[i], i === idx);
    }
  }

  /**
   * Kill ScrollTrigger without reverting pin DOM — revert uses removeChild and throws
   * NotFoundError when Shopify / morph has already detached nodes (common in editor).
   * @param {import('gsap/ScrollTrigger').ScrollTrigger | undefined} st
   */
  #safeKillScrollTrigger(st) {
    if (!st) return;
    try {
      st.kill(false);
    } catch {
      /* ignore */
    }
  }

  #destroyScrollDriven() {
    this.#scrollLifecycle++;
    if (this.#scrollRafId != null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = undefined;
    }
    const st = this.#scrollTrigger;
    this.#scrollTrigger = undefined;
    this.#safeKillScrollTrigger(st);
    this.#lastSyncedScrollIndex = -1;
    this.#lastScrollProgress = -1;
    this.removeAttribute('data-scroll-active');
  }

  async #initScrollDriven() {
    if (typeof window === 'undefined' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    const lifecycle = this.#scrollLifecycle;

    const rows = this.#getRows();
    if (rows.length === 0) return;

    const gsapSrc = this.dataset.gsapSrc;
    const stSrc = this.dataset.stSrc;
    if (!gsapSrc || !stSrc) {
      console.warn('[collection-accordion-rows] Missing data-gsap-src / data-st-src (enable scroll in theme editor and redeploy assets)');
      return;
    }

    const vh = Number.parseFloat(this.dataset.scrollRowVh || '1');
    const rowVh = Number.isFinite(vh) && vh > 0 ? vh : 1;

    try {
      await carEnsureGsap(gsapSrc, stSrc);
    } catch (err) {
      console.warn('[collection-accordion-rows] GSAP failed to load', err);
      return;
    }

    if (lifecycle !== this.#scrollLifecycle || !this.isConnected) {
      return;
    }

    const ScrollTrigger = window.ScrollTrigger;
    if (!ScrollTrigger) {
      console.warn('[collection-accordion-rows] ScrollTrigger not on window');
      return;
    }

    const endDistance = () => `+=${Math.max(1, rows.length) * window.innerHeight * rowVh}`;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = undefined;
      if (lifecycle !== this.#scrollLifecycle || !this.isConnected) {
        return;
      }

      this.#safeKillScrollTrigger(this.#scrollTrigger);
      this.setAttribute('data-scroll-active', 'true');
      this.#scrollTrigger = ScrollTrigger.create({
        trigger: this,
        start: 'top top',
        end: endDistance,
        pin: true,
        pinSpacing: true,
        scrub: 0.35,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          if (!this.isConnected) return;
          this.#syncRowFromScrollProgress(self.progress);
        },
      });

      this.#scrollTrigger.refresh();

      // Avoid global refresh here: it re-measures all triggers and can call revert/kill
      // while morph/editor is mid-DOM update, causing NotFoundError in _swapPinOut.
      this.#syncRowFromScrollProgress(this.#scrollTrigger.progress);
    });
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const trigger = event.target.closest('[data-accordion-trigger]');
    if (!(trigger instanceof HTMLElement) || !this.contains(trigger)) return;

    const row = trigger.closest('[data-accordion-row]');
    if (!(row instanceof HTMLElement)) return;

    const expanded = row.getAttribute('data-expanded') === 'true';

    if (expanded) {
      for (const r of this.#getRows()) {
        this.#setRowExpanded(r, false);
      }
      this.#lastSyncedScrollIndex = -1;
    } else {
      this.#expandExclusive(row);
      const rows = this.#getRows();
      this.#lastSyncedScrollIndex = rows.indexOf(row);
    }
  };
}

if (!customElements.get('collection-accordion-rows')) {
  customElements.define('collection-accordion-rows', CollectionAccordionRows);
}
